import React from "react";
import Button from "components/button";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import { Controller } from "react-hook-form";
import DatePicker from "components/date-picker";

interface FleetDocumentTabProps {
  form: any;
  t: any;
  fields: any[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  isUploadingDoc: Record<number, boolean>;
  onUpload: (index: number, file: File) => void;
  expiryWarnings: Record<number, string | null>;
}

const documentTypeOptions = [
  { label: "STNK", value: "STNK" },
  { label: "KIR", value: "KIR" },
  { label: "BPKB", value: "BPKB" },
  { label: "Lainnya", value: "Additional" },
];

const DocumentUploadField = ({
  index,
  label,
  placeholder,
  value,
  onChange,
  error,
  helperText,
  buttonText,
  isUploading,
  originalName,
}: any) => (
  <div className="relative">
    <input
      type="file"
      className="hidden"
      id={`file-upload-${index}`}
      accept=".jpg,.jpeg,.png,.pdf"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) onChange(file);
        e.target.value = "";
      }}
    />
    <Input
      label={label}
      placeholder={placeholder}
      readOnly
      className="pr-2"
      value={originalName || (typeof value === "string" ? value.split("/").pop() : value instanceof File ? value.name : "")}
      error={error}
      helperText={helperText}
      rightIcon={
        <Button
          type="button"
          variant="fill"
          color={isUploading ? "secondary" : "primary"}
          size="small"
          className="h-[26px] px-2 text-[10px] leading-none whitespace-nowrap min-w-[70px]"
          onClick={() => document.getElementById(`file-upload-${index}`)?.click()}
          disabled={isUploading}
          data-testid={`btn-document-upload-${index}`}
        >
          {isUploading ? <Icon name="refresh" className="animate-spin" size={14} /> : buttonText}
        </Button>
      }
    />
  </div>
);

const FleetDocumentTab = ({
  form,
  t,
  fields,
  onAdd,
  onRemove,
  isUploadingDoc,
  onUpload,
  expiryWarnings,
}: FleetDocumentTabProps) => {
  const {
    control,
    watch,
  } = form;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6">
        {fields.map((item, index) => {
          const usedByOthers = fields
            .filter((_, i) => i !== index)
            .map((_, i) => {
              const otherIndex = i < index ? i : i + 1;
              return watch(`documents.${otherIndex}.name`);
            })
            .filter(Boolean);

          const availableOptions = documentTypeOptions.filter(
            (opt) => !usedByOthers.includes(opt.value) || opt.value === watch(`documents.${index}.name`)
          );

          return (
          <div key={item.id} className="grid grid-cols-[repeat(6,1fr)_40px] gap-6 items-start pb-6 border-b border-whiteScale-80 last:border-0 last:pb-0">
            <div className="col-span-2">
              <Controller
                name={`documents.${index}.name`}
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    label={t("label-document-name")}
                    required={index < 3}
                    options={availableOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    error={fieldState.error}
                    placeholder={t("placeholder-select")}
                    data-testid={`select-document-name-${index}`}
                  />
                )}
              />
            </div>

            <div className="col-span-2">
              <Controller
                name={`documents.${index}.expiryDate`}
                control={control}
                render={({ field, fieldState }) => {
                  const docName = watch(`documents.${index}.name`);
                  const hasExpiry = docName === "STNK" || docName === "KIR";
                  
                  if (!hasExpiry) return <div className="h-[62px]" />;

                  const label = docName === "STNK" ? t("label-stnk-expiry") : 
                              docName === "KIR" ? t("label-kir-expiry") : 
                              t("label-expired-date");

                  return (
                    <DatePicker
                      label={`${label} *`}
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error}
                      helperText={!fieldState.error && expiryWarnings[index] ? expiryWarnings[index] : undefined}
                      placeholder="dd / mm / yyyy"
                      data-testid={`datepicker-document-expiry-${index}`}
                    />
                  );
                }}
              />
            </div>

            <div className="col-span-2">
              <Controller
                name={`documents.${index}.file`}
                control={control}
                render={({ field, fieldState }) => (
                  <DocumentUploadField
                    index={index}
                    label={`${t("label-upload-document")} ${index < 3 ? "*" : ""}`}
                    placeholder={t("placeholder-upload-document")}
                    value={field.value}
                    originalName={watch(`documents.${index}.originalName`)}
                    onChange={(file: File) => {
                      field.onChange(file);
                      if (onUpload) onUpload(index, file);
                    }}
                    error={fieldState.error}
                    helperText={t("upload-document-helper")}
                    buttonText={t("btn-upload-file")}
                    isUploading={isUploadingDoc[index]}
                  />
                )}
              />
            </div>

            <div className="pt-8 flex justify-center">
              {index > 2 && (
                <button
                  type="button"
                  className="text-error-500 hover:text-error-600 transition-colors bg-error-50 p-2 rounded-lg"
                  onClick={() => onRemove(index)}
                >
                  <Icon name="minus-cirlce" size={24} />
                </button>
              )}
            </div>
          </div>
          );
        })}

        <button
          type="button"
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors font-bold text-sm w-fit"
          onClick={onAdd}
        >
          <Icon name="add-circle" size={20} />
          {t("btn-add-document")}
        </button>
      </div>
    </div>
  );
};

export default FleetDocumentTab;
